// Owner: ai-client (wave 1), copilot-panel (wave 2). The workspace rail's AI slot (plan §4.5, `ai.md` F-19):
// the rail's AI entry point exists only where AI works. Members see nothing while AI is off or not
// connected (the slot collapses), admins see the screen's one "Connect AI" link instead, and with AI
// ready (or its budget used up, which the entry point itself explains) the slot shows its children.
// Light module: it renders in the rail on every screen.
import { Link } from '@tanstack/react-router';
import type { CSSProperties, ReactNode } from 'react';
import type { AiCapability } from '@dataroom/contracts/ai/status';
import { Icon } from '@dataroom/ui';
import { t } from '../lib/i18n';
import { useAiAvailability } from './availability';

export type AiRailSlotProps = {
  /** The entry point while AI works (the launcher). */
  children: ReactNode;
  /** The capability the entry point uses. */
  capability?: AiCapability;
  className?: string;
  style?: CSSProperties;
  /** Class of the "Connect AI" link, so it matches the rail's own controls. */
  connectClassName?: string;
};

export function AiRailSlot({
  children,
  capability = 'agent',
  className,
  style,
  connectClassName,
}: AiRailSlotProps) {
  const availability = useAiAvailability(capability);
  if (availability.state === 'hidden') return null;
  return (
    <div className={className} style={style} data-testid="ai-rail-slot" data-state={availability.state}>
      {availability.state === 'connect' ? (
        <Link
          to="/settings/ai"
          className={connectClassName}
          style={{ textDecoration: 'none' }}
          data-testid="ai-connect"
        >
          <Icon name="sparkle" size={15} />
          <span style={{ flexGrow: 1 }}>{t('aiClient.ask.connect')}</span>
          <Icon name="chevron-right" size={14} />
        </Link>
      ) : availability.state === 'loading' ? null : (
        children
      )}
    </div>
  );
}
